import type { LoadingStates } from 'Store'
import { createAxiosAsyncThunk, getAxiosInstance, type ValidationErrors } from 'Util/ApiClient'
import actionGenerator from 'Util/actionGenerator'
import { IS_HSL_SERVICE_ENVIRONMENT } from 'Util/constants'
import SessionUtil from 'Util/sessionManager'
import { type AnyAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios, { type AxiosError } from 'axios'

const initialState: UserState = {
  loading: 'pending', // User needs to be fetched at the very beginning, before we render the layout. If we set this initially to idle and wait for the fetchCurrentUser action to set it to pending, the layout gets rendered a little while and that causes problems with protected routes
  authed: !SessionUtil.isEmpty(),
  user: null
}

export interface UserState {
  loading: LoadingStates
  authed: boolean
  user: UserShape | null
}

export enum GroupType {
  authoritySuperuser = 'authority_superuser',
  authorityAdmin = 'authority_admin',
  authorityUser = 'authority_user',
  spmUser = 'spm_user'
}

export interface UserShape {
  id: number
  email: string
  groups: GroupType[]
  pta: string
}

export const isSuperuser = (user: UserShape | null) => {
  if (!user) return false
  return user.groups.includes(GroupType.authoritySuperuser)
}

export const isAuthorityAdmin = (user: UserShape | null) => {
  if (!user) return false
  return user.groups.includes(GroupType.authorityAdmin)
}

export const isAuthorityUser = (user: UserShape | null) => {
  if (!user) return false
  return user.groups.includes(GroupType.authorityUser)
}

export const isSPMUser = (user: UserShape | null) => {
  if (!user) return false
  return user.groups.includes(GroupType.spmUser)
}

export const isSuperuserOrSPMUser = (user: UserShape | null) => {
  return isSuperuser(user) || isSPMUser(user)
}

export const canAccessProductManagement = (user: UserShape | null) => {
  if (!user) return false
  // Superusers are allowed to access all services
  if (isSuperuser(user)) return true
  // Authority admins are allowed to access product management
  return isAuthorityAdmin(user)
}

export const canAccessSalesManagement = (user: UserShape | null) => {
  if (!user) return false
  // HSL users are not allowed to access sales management
  if (IS_HSL_SERVICE_ENVIRONMENT) return false
  return isSuperuser(user) || isAuthorityAdmin(user) || isAuthorityUser(user)
}

export const canAccessServiceProviderManagement = (user: UserShape | null) => {
  if (!user) return false
  return isSuperuserOrSPMUser(user)
}

export const canAccessControlDataManagement = (user: UserShape | null) => {
  if (!user) return false
  return isSuperuser(user)
}

type FetchCurrentUserResponse = UserShape

const fetchCurrentUser = createAxiosAsyncThunk<FetchCurrentUserResponse, undefined>(
  'user/fetchCurrentUser',
  '/api/v1/users/current/'
)

type LogOutResponse = Record<string, unknown>

const logOutUser = createAsyncThunk<
  LogOutResponse,
  undefined,
  {
    rejectValue: ValidationErrors
  }
>('user/logOut', async (_data, { rejectWithValue }) => {
  try {
    const response = await getAxiosInstance().post<LogOutResponse>(
      `/api/oidc/logout/`,
      JSON.stringify([])
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

const genericActions = actionGenerator('user')

const userSlice = createSlice({
  name: genericActions.name,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchCurrentUser.fulfilled, (state, { payload }) => {
        state.user = payload
      })
      .addCase(logOutUser.fulfilled, state => {
        SessionUtil.remove()
        state.authed = false
        state.user = null
      })
      .addCase(logOutUser.rejected, state => {
        state.authed = false
        state.user = null
      })
      .addCase(genericActions.ResetAction, () => initialState)
      .addMatcher(genericActions.isPendingAction, state => {
        state.loading = 'pending'
      })
      .addMatcher(genericActions.isRejectedAction, state => {
        state.loading = 'rejected'
      })
      .addMatcher(genericActions.isFulfilledAction, state => {
        state.loading = 'fulfilled'
      })
      .addMatcher(
        (action: AnyAction) => action.type.endsWith('/rejected'),
        (state, { payload }) => {
          if (
            payload?.detail === 'Autentikaatiotunnuksia ei annettu.' ||
            payload?.detail === 'Authentication credentials were not provided.'
          ) {
            SessionUtil.remove()
            state.authed = false
          }
        }
      )
  }
})

export default userSlice.reducer

export { fetchCurrentUser, logOutUser }
